import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  Building2,
  TrendingUp,
  BarChart3,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Upload,
  AlertCircle,
  Check,
  Clock,
  Target,
  Beaker,
  FileText,
  CalendarDays,
  Zap
} from 'lucide-react';
import api from '../../services/api';

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Get significance color
const getSignificanceColor = (score) => {
  if (score >= 8) return 'bg-red-100 text-red-700';
  if (score >= 6) return 'bg-orange-100 text-orange-700';
  if (score >= 4) return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-700';
};

// Get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'upcoming': return 'bg-blue-100 text-blue-700';
    case 'completed': return 'bg-green-100 text-green-700';
    case 'delayed': return 'bg-yellow-100 text-yellow-700';
    case 'cancelled': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// Stats Card Component
function StatsCard({ title, value, icon: Icon, color = 'indigo', subtitle }) {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

// Edit/Add Modal Component
function CatalystFormModal({ isOpen, onClose, catalyst, onSave }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (catalyst) {
      setFormData({
        company_name: catalyst.company_name || '',
        event_type: catalyst.event_type || '',
        event_title: catalyst.event_title || '',
        event_description: catalyst.event_description || '',
        expected_date: catalyst.expected_date ? catalyst.expected_date.split('T')[0] : '',
        date_precision: catalyst.date_precision || 'exact',
        quarter: catalyst.quarter || '',
        therapeutic_area: catalyst.therapeutic_area || '',
        indication: catalyst.indication || '',
        modality: catalyst.modality || '',
        target: catalyst.target || '',
        asset_name: catalyst.asset_name || '',
        trial_phase: catalyst.trial_phase || '',
        significance_score: catalyst.significance_score || 5,
        market_impact: catalyst.market_impact || 'moderate',
        source_name: catalyst.source_name || '',
        source_url: catalyst.source_url || '',
        status: catalyst.status || 'upcoming',
        is_verified: catalyst.is_verified || false
      });
    } else {
      setFormData({
        company_name: '',
        event_type: '',
        event_title: '',
        event_description: '',
        expected_date: '',
        date_precision: 'exact',
        quarter: '',
        therapeutic_area: '',
        indication: '',
        modality: '',
        target: '',
        asset_name: '',
        trial_phase: '',
        significance_score: 5,
        market_impact: 'moderate',
        source_name: '',
        source_url: '',
        status: 'upcoming',
        is_verified: false
      });
    }
    setError(null);
  }, [catalyst, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onSave(catalyst?.id, formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const eventTypes = [
    'Phase 1 Data', 'Phase 2 Data', 'Phase 3 Data', 'Pivotal Data',
    'FDA Decision', 'PDUFA Date', 'Advisory Committee', 'EMA Decision',
    'Conference Presentation', 'Clinical Trial Update', 'IND Filing',
    'NDA/BLA Filing', 'Approval', 'Label Expansion'
  ];

  const therapeuticAreas = [
    'Oncology', 'Immunology', 'Neurology', 'Cardiovascular', 'Metabolic',
    'Rare Disease', 'Infectious Disease', 'Respiratory', 'Ophthalmology',
    'Dermatology', 'Hematology', 'GI/Hepatology', 'MASH/NASH', 'Nephrology'
  ];

  const modalities = [
    'Small Molecule', 'Antibody', 'ADC', 'Bispecific', 'CAR-T', 'Cell Therapy',
    'Gene Therapy', 'mRNA', 'siRNA', 'Peptide', 'Protein', 'Vaccine', 'Radiopharmaceutical'
  ];

  const trialPhases = ['Preclinical', 'Phase 1', 'Phase 1/2', 'Phase 2', 'Phase 2/3', 'Phase 3', 'Pivotal', 'Filed', 'Approved'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{catalyst ? 'Edit Catalyst' : 'Add Catalyst'}</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Company & Event Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
              <select
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="">Select Type</option>
                {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Event Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Title *</label>
            <input
              type="text"
              value={formData.event_title}
              onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., AbbVie epcoritamab Phase 3 DLBCL data"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.event_description}
              onChange={(e) => setFormData({ ...formData, event_description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Date & Precision */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Date</label>
              <input
                type="date"
                value={formData.expected_date}
                onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Precision</label>
              <select
                value={formData.date_precision}
                onChange={(e) => setFormData({ ...formData, date_precision: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="exact">Exact Date</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="half">Half Year</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quarter (if approx)</label>
              <input
                type="text"
                value={formData.quarter}
                onChange={(e) => setFormData({ ...formData, quarter: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., Q1 2025"
              />
            </div>
          </div>

          {/* Clinical Context */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Area</label>
              <select
                value={formData.therapeutic_area}
                onChange={(e) => setFormData({ ...formData, therapeutic_area: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select Area</option>
                {therapeuticAreas.map(ta => <option key={ta} value={ta}>{ta}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Indication</label>
              <input
                type="text"
                value={formData.indication}
                onChange={(e) => setFormData({ ...formData, indication: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., DLBCL, NSCLC"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
              <select
                value={formData.modality}
                onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select Modality</option>
                {modalities.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
              <input
                type="text"
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., HER2, PD-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name</label>
              <input
                type="text"
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., epcoritamab"
              />
            </div>
          </div>

          {/* Trial Phase & Significance */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trial Phase</label>
              <select
                value={formData.trial_phase}
                onChange={(e) => setFormData({ ...formData, trial_phase: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select Phase</option>
                {trialPhases.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Significance: {formData.significance_score}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.significance_score}
                onChange={(e) => setFormData({ ...formData, significance_score: parseInt(e.target.value) })}
                className="w-full mt-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Market Impact</label>
              <select
                value={formData.market_impact}
                onChange={(e) => setFormData({ ...formData, market_impact: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="low">Low</option>
                <option value="moderate">Moderate</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Source & Status */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
              <input
                type="text"
                value={formData.source_name}
                onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., BiopharmCatalyst"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source URL</label>
              <input
                type="url"
                value={formData.source_url}
                onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
                <option value="delayed">Delayed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Verified */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_verified"
              checked={formData.is_verified}
              onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
            />
            <label htmlFor="is_verified" className="text-sm text-gray-700">Mark as verified</label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {catalyst ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Import Modal Component
function ImportModal({ isOpen, onClose, onImport }) {
  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const events = JSON.parse(jsonText);
      if (!Array.isArray(events)) {
        throw new Error('JSON must be an array of catalyst events');
      }
      const importResult = await onImport(events);
      setResult(importResult);
      if (importResult.imported > 0) {
        setTimeout(() => {
          onClose();
          setJsonText('');
          setResult(null);
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Import Catalyst Events</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Paste JSON array of catalyst events
            </label>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              placeholder={`[
  {
    "company": "AbbVie",
    "event": "Phase 3 DLBCL data",
    "date": "2025-03-15",
    "therapeutic_area": "Oncology",
    "modality": "Bispecific",
    "trial_phase": "Phase 3"
  }
]`}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {result && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
              result.errors > 0 ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              <Check className="w-4 h-4" />
              Imported {result.imported} events. {result.errors > 0 && `${result.errors} errors.`}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !jsonText.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Catalysts Page
export default function AdminCatalysts() {
  const [catalysts, setCatalysts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [selectedTA, setSelectedTA] = useState('');
  const [selectedModality, setSelectedModality] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('upcoming');
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [editingCatalyst, setEditingCatalyst] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Fetch catalysts
  const fetchCatalysts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });
      if (search) params.append('search', search);
      if (selectedTA) params.append('therapeutic_area', selectedTA);
      if (selectedModality) params.append('modality', selectedModality);
      if (selectedStatus) params.append('status', selectedStatus);

      const response = await api.get(`/admin/catalysts?${params}`);
      setCatalysts(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.pagination?.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch catalysts:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, search, selectedTA, selectedModality, selectedStatus]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/catalysts/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchCatalysts();
  }, [fetchCatalysts]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, offset: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Save catalyst
  const saveCatalyst = async (id, data) => {
    if (id) {
      await api.put(`/admin/catalysts/${id}`, data);
    } else {
      await api.post('/admin/catalysts', data);
    }
    fetchCatalysts();
    fetchStats();
  };

  // Delete catalyst
  const deleteCatalyst = async (catalyst) => {
    if (!window.confirm(`Delete catalyst: ${catalyst.event_title}?`)) return;
    try {
      await api.delete(`/admin/catalysts/${catalyst.id}`);
      fetchCatalysts();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Import catalysts
  const importCatalysts = async (events) => {
    const response = await api.post('/catalysts/import', { events });
    fetchCatalysts();
    fetchStats();
    return response.data.data;
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  const therapeuticAreas = [
    'Oncology', 'Immunology', 'Neurology', 'Cardiovascular', 'Metabolic',
    'Rare Disease', 'Infectious Disease', 'Respiratory', 'Ophthalmology',
    'Dermatology', 'Hematology', 'GI/Hepatology', 'MASH/NASH', 'Nephrology'
  ];

  const modalities = [
    'Small Molecule', 'Antibody', 'ADC', 'Bispecific', 'CAR-T', 'Cell Therapy',
    'Gene Therapy', 'mRNA', 'siRNA', 'Peptide'
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalyst Events</h1>
          <p className="text-sm text-gray-500">Manage upcoming clinical catalysts and data readouts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => { setEditingCatalyst(null); setShowEditModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Add Catalyst
          </button>
          <button
            onClick={() => { fetchCatalysts(); fetchStats(); }}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <StatsCard title="Total Upcoming" value={stats.upcoming_total || 0} icon={CalendarDays} color="indigo" />
          <StatsCard title="Next 30 Days" value={stats.next_30_days || 0} icon={Clock} color="amber" />
          <StatsCard title="Next 90 Days" value={stats.next_90_days || 0} icon={Calendar} color="blue" />
          <StatsCard title="Major Catalysts" value={stats.major_catalysts || 0} icon={Zap} color="red" subtitle="Score 8+" />
          <StatsCard title="Therapeutic Areas" value={stats.therapeutic_areas || 0} icon={Beaker} color="green" />
          <StatsCard title="Companies" value={stats.companies || 0} icon={Building2} color="indigo" />
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search catalysts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'text-gray-600'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
            <select
              value={selectedTA}
              onChange={(e) => { setSelectedTA(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Therapeutic Areas</option>
              {therapeuticAreas.map(ta => <option key={ta} value={ta}>{ta}</option>)}
            </select>

            <select
              value={selectedModality}
              onChange={(e) => { setSelectedModality(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Modalities</option>
              {modalities.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="delayed">Delayed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button
              onClick={() => {
                setSelectedTA('');
                setSelectedModality('');
                setSelectedStatus('upcoming');
                setSearch('');
              }}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Catalysts Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Event</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">TA / Modality</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Phase</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading...
                  </td>
                </tr>
              ) : catalysts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No catalysts found</td>
                </tr>
              ) : (
                catalysts.map((catalyst) => (
                  <tr key={catalyst.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-900">{formatDate(catalyst.expected_date)}</span>
                      </div>
                      {catalyst.quarter && (
                        <span className="text-xs text-gray-500">{catalyst.quarter}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900 truncate max-w-[120px]">
                          {catalyst.company_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-[200px]">
                        <p className="text-sm font-medium text-gray-900 truncate">{catalyst.event_title}</p>
                        <p className="text-xs text-gray-500">{catalyst.event_type}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {catalyst.therapeutic_area && (
                          <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                            {catalyst.therapeutic_area}
                          </span>
                        )}
                        {catalyst.modality && (
                          <span className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full ml-1">
                            {catalyst.modality}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {catalyst.trial_phase || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getSignificanceColor(catalyst.significance_score)}`}>
                        {catalyst.significance_score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(catalyst.status)}`}>
                        {catalyst.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => { setEditingCatalyst(catalyst); setShowEditModal(true); }}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteCatalyst(catalyst)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                disabled={pagination.offset === 0}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <CatalystFormModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingCatalyst(null); }}
        catalyst={editingCatalyst}
        onSave={saveCatalyst}
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={importCatalysts}
      />
    </div>
  );
}
